import { useCallback, useEffect, useState } from 'react';

export type LocationSource = 'gps' | 'ip';

export interface CurrentLocation {
  lat: number;
  lon: number;
  accuracy?: number;
  source: LocationSource;
  city?: string;
  region?: string;
  country?: string;
}

interface UseCurrentLocationResult {
  location: CurrentLocation | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

const GPS_TIMEOUT_MS = 10000;

async function fetchLocationByIP(): Promise<CurrentLocation> {
  const response = await fetch('https://ipwho.is/');
  if (!response.ok) {
    throw new Error('Falha ao obter localização por IP');
  }

  const data = await response.json();
  if (!data.success) {
    throw new Error(data.message || 'Falha ao obter localização por IP');
  }

  return {
    lat: data.latitude,
    lon: data.longitude,
    source: 'ip',
    city: data.city,
    region: data.region,
    country: data.country,
  };
}

function getLocationByGPS(): Promise<CurrentLocation> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocalização não suportada'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lon: position.coords.longitude,
          accuracy: position.coords.accuracy,
          source: 'gps',
        });
      },
      (error) => reject(error),
      {
        enableHighAccuracy: true,
        timeout: GPS_TIMEOUT_MS,
        maximumAge: 60000,
      }
    );
  });
}

export function useCurrentLocation(enabled = true): UseCurrentLocationResult {
  const [location, setLocation] = useState<CurrentLocation | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => {
    setRefreshKey((key) => key + 1);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const resolveLocation = async () => {
      setLoading(true);
      setError(null);

      try {
        const gpsLocation = await getLocationByGPS();
        if (!cancelled) {
          setLocation(gpsLocation);
        }
      } catch {
        try {
          const ipLocation = await fetchLocationByIP();
          if (!cancelled) {
            setLocation(ipLocation);
          }
        } catch (ipError) {
          if (!cancelled) {
            setError(
              ipError instanceof Error
                ? ipError.message
                : 'Não foi possível determinar sua localização'
            );
          }
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    resolveLocation();

    return () => {
      cancelled = true;
    };
  }, [enabled, refreshKey]);

  return { location, loading, error, refresh };
}
